'use client';

import { useId, type ReactNode } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { Icon } from '../shared/Icon';
import {
  childLevel,
  headingLevel,
  items,
  SkLink,
  surfaceClass,
  text,
  type HeadingLevel,
  type Localized,
  type Surface,
} from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * Door styles explained: one card per construction (slab, five-piece shaker,
 * routed), each with a line drawing of the front, a short description, and the
 * corner joints that style can be ordered with, each with its own small
 * drawing.
 *
 * <p>Drawings are inline SVG in the theme's line weight, not images, so they
 * stay crisp, follow the palette, and need no asset. They are decorative
 * (`aria-hidden`); the style and joint names carry the meaning as text.
 */
export type DoorDrawing = 'slab' | 'shaker' | 'routed';
export type JointDrawing = 'square' | 'mitred' | 'mortise-and-tenon';

export interface SpatialKitchenDoorJoint {
  joint?: JointDrawing;
  name?: Localized;
  note?: Localized;
}

export interface SpatialKitchenDoorStyle {
  drawing?: DoorDrawing;
  name?: Localized;
  body?: Localized;
  joints?: SpatialKitchenDoorJoint[];
  href?: string;
  linkLabel?: Localized;
  /**
   * A from-price for the style, written out — "From £29".
   *
   * <p>This page explains three constructions very well and, without a number against them,
   * reads as an article rather than a shop. The price is what turns "here is what a shaker door
   * is" into "here is what a shaker door costs", which is the question the visitor arrived
   * with.
   */
  price?: Localized;
  /** Lead time for the style, e.g. "Stock 5–7 days". */
  lead?: Localized;
}

export interface SpatialKitchenDoorStylesProps {
  eyebrow?: Localized;
  title?: Localized;
  intro?: Localized;
  styles?: SpatialKitchenDoorStyle[];
  jointsLabel?: Localized;
  surface?: Surface;
  headingLevel?: HeadingLevel;
}

const STROKE = 'currentColor';

function DoorSvg({ drawing }: { drawing: DoorDrawing }) {
  let inner: ReactNode = null;
  if (drawing === 'shaker') {
    inner = (
      <>
        <rect x="46" y="38" width="68" height="124" />
        <path d="M34 38h92M34 162h92" opacity=".45" />
      </>
    );
  } else if (drawing === 'routed') {
    inner = (
      <path d="M52 40h56a8 8 0 0 0 8 8v104a8 8 0 0 0-8 8H52a8 8 0 0 0-8-8V48a8 8 0 0 0 8-8Z" />
    );
  }
  return (
    <svg viewBox="0 0 160 200" width="160" height="200" aria-hidden="true" focusable="false">
      <g fill="none" stroke={STROKE} strokeWidth="1.5" strokeLinejoin="round">
        <rect x="26" y="22" width="108" height="156" rx="1" />
        {inner}
        <path d="M118 96v20" strokeWidth="3" strokeLinecap="round" />
      </g>
    </svg>
  );
}

function JointSvg({ joint }: { joint: JointDrawing }) {
  let detail: ReactNode;
  if (joint === 'mitred') {
    detail = <path d="M6 6l20 20" />;
  } else if (joint === 'mortise-and-tenon') {
    detail = (
      <>
        <path d="M26 6v42" />
        <path d="M26 14h14v10H26" strokeDasharray="3 2" />
      </>
    );
  } else {
    detail = <path d="M26 6v42" />;
  }
  return (
    <svg viewBox="0 0 56 56" width="40" height="40" aria-hidden="true" focusable="false">
      <g fill="none" stroke={STROKE} strokeWidth="1.5" strokeLinejoin="round">
        <path d="M6 50V6h44" />
        <path d="M26 50V26h24" />
        {detail}
      </g>
    </svg>
  );
}

const CSS = `
.sk-doors__grid{display:grid;gap:1.5rem}
@media (min-width:769px){.sk-doors__grid{grid-template-columns:repeat(3,minmax(0,1fr))}}
.sk-door{display:grid;gap:1rem;align-content:start;background:var(--sk-white);border:1px solid var(--sk-border);border-radius:8px;padding:clamp(1.5rem,2vw,2rem)}
.sk-door__drawing{display:grid;place-items:center;background:var(--sk-warm);border-radius:0 32px;padding:1.5rem;color:var(--sk-ink)}
.sk-door__drawing svg{width:min(100%,9rem);height:auto}
.sk-door__name{font-size:1.5rem}
/* Sits directly under the name, above the prose, so the number is read with the style rather
   than found at the end of a paragraph. */
.sk-door__facts{display:flex;flex-wrap:wrap;align-items:baseline;gap:.25rem .625rem;margin:-.5rem 0 0;font-size:.9375rem}
.sk-door__price{font-weight:600;color:var(--sk-ink)}
.sk-door__lead{color:var(--sk-muted)}
.sk-door__lead::before{content:'';display:inline-block;width:1px;height:.8em;margin-right:.625rem;background:currentColor;opacity:.4}
.sk-door__facts .sk-door__lead:first-child::before{display:none}
.sk-door__joints-label{margin:.5rem 0 0;font-size:.875rem;font-weight:500;color:var(--sk-ink)}
.sk-door__joints{display:grid;gap:.5rem;margin:0;padding:0;list-style:none}
.sk-joint{display:grid;grid-template-columns:auto 1fr;align-items:center;gap:.75rem;padding:.5rem .75rem;border-radius:999px;background:var(--sk-sage-grey);color:var(--sk-ink)}
.sk-joint svg{color:var(--sk-sage-deep);width:2rem;height:2rem}
.sk-joint__name{font-size:.9375rem;font-weight:500;line-height:1.3}
.sk-joint__note{display:block;font-size:.8125rem;font-weight:400;color:var(--sk-muted)}
`;

export function SpatialKitchenDoorStyles(props: SpatialKitchenDoorStylesProps) {
  const styles = items<SpatialKitchenDoorStyle>(props.styles).slice(0, 3);
  const title = text(props.title, 'Door styles');
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const CardTitle = childLevel(level);
  const jointsLabel = text(props.jointsLabel, 'Available joints');
  const titleId = useId();

  return (
    <BlockRoot
      as="section"
      className={`sk sk-doors sk-section ${surfaceClass(props.surface, 'warm')}`}
      aria-labelledby={titleId}
    >
      <SkBaseStyles />
      <SkStyles id="door-styles" css={CSS} />
      <div className="sk-container">
        <div className="sk-section-head">
          {text(props.eyebrow) && <p className="sk-eyebrow">{text(props.eyebrow)}</p>}
          <Title id={titleId} className="sk-heading sk-h2">
            {title}
          </Title>
          {text(props.intro) && <p className="sk-lead">{text(props.intro)}</p>}
        </div>
        <ul className="sk-doors__grid sk-list-reset">
          {styles.map((style, index) => {
            const drawing: DoorDrawing =
              style.drawing === 'shaker' || style.drawing === 'routed' ? style.drawing : 'slab';
            const name = text(style.name);
            const joints = items<SpatialKitchenDoorJoint>(style.joints);
            const linkLabel = text(style.linkLabel);
            return (
              <li key={`${name}-${index}`} className="sk-door">
                <div className="sk-door__drawing">
                  <DoorSvg drawing={drawing} />
                </div>
                <CardTitle className="sk-heading sk-door__name">{name}</CardTitle>
                {(text(style.price) || text(style.lead)) && (
                  <p className="sk-door__facts">
                    {text(style.price) && (
                      <span className="sk-door__price">{text(style.price)}</span>
                    )}
                    {text(style.lead) && <span className="sk-door__lead">{text(style.lead)}</span>}
                  </p>
                )}
                {text(style.body) && <p className="sk-body">{text(style.body)}</p>}
                {joints.length > 0 && (
                  <>
                    <p className="sk-door__joints-label">{jointsLabel}</p>
                    <ul className="sk-door__joints">
                      {joints.map((joint, jointIndex) => {
                        const kind: JointDrawing =
                          joint.joint === 'mitred' || joint.joint === 'mortise-and-tenon' ? joint.joint : 'square';
                        return (
                          <li key={`${kind}-${jointIndex}`} className="sk-joint">
                            <JointSvg joint={kind} />
                            <span className="sk-joint__name">
                              {text(joint.name)}
                              {text(joint.note) && <span className="sk-joint__note">{text(joint.note)}</span>}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
                {linkLabel && style.href && (
                  <p style={{ margin: '.25rem 0 0' }}>
                    <SkLink href={style.href} className="sk-textlink" aria-label={`${linkLabel}: ${name}`}>
                      {linkLabel}
                      <Icon name="arrow-right" size={16} />
                    </SkLink>
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </BlockRoot>
  );
}
